import React, {useMemo} from "react";
import {MapObject} from "../../../../../Server/DB/mapObjects/@MapObject.js";
import {DMap} from "../../../../../Server/DB/maps/@Map.js";
import {MapState} from "../../../../../Store/main/maps/@MapState.js";
import {observer_mgl_r3} from "../../../../../Utils/ThreeJS/General.js";
import {Vec2, Vec3} from "../../../../../Utils/ThreeJS/VectorStructs.js";
import {store} from "../../../../../Store/index.js";
import {ObjPath} from "../../../../../Server/DB/mapObjects/@MapObject/ObjPath.js";
import {PathFill} from "../../../../../Server/DB/mapObjects/@MapObject/ObjPath/PathFill.js";
import {ResolvePathMeshes} from "../../../../../Server/DB/mapObjects/@MapObject/ObjPath/ObjPathResolvers.js";
import {GetEntity} from "../../../../../Server/DB/entities.js";
import {useBase64ImageStrAsTexture} from "../../../../../Utils/ThreeJS/3JSHooks.js";
import {ColorArrayToTHREEColor} from "../../../../../Utils/ThreeJS/ColorUtils.js";
import {CalculateUVsAsPointPositions} from "./MeshUI.js";
import {ExtrudeGeometry, ShapeGeometry} from "three";
import * as THREE from "three";

/** Render the fill-mesh (a closed polygon mesh over the path's resolved points) for a path
 *  that has a PathFill config. Mirrors the mesh-styling behaviour of the (to-be-retired)
 *  ObjMesh/MeshUI pipeline: color, opacity, texture, texture-size, texture-rotation, z-offset. */
export const PathFillUI = observer_mgl_r3((props: {fill: PathFill, path: ObjPath, obj: MapObject, map: DMap, mapState: MapState})=>{
	const {fill, path, obj} = props;
	const pathIndex = obj.paths.indexOf(path);
	const toolState = store.main.maps.tools.toolStates.pathModify;

	const pendingPathUpdatesHere = toolState.pendingObjPathUpdates.get(obj.id)?.pathIndex == pathIndex ? toolState.pendingObjPathUpdates.get(obj.id) : null;

	// Pending edits take precedence: temporarily show just the edited literal points (hiding
	// any derived-path polygons until the edit completes). Mirrors PathUI's pending-edit handling.
	const pathAndObj_final = useMemo(()=>{
		if (pendingPathUpdatesHere == null) return {path, obj};
		const path_final = {...path, points: pendingPathUpdatesHere.NewPoints()};
		// reconstruct `obj_final` to include `path_final`, so `obj.paths.indexOf(path)` works downstream
		const paths_final = [...obj.paths];
		paths_final[pathIndex] = path_final;
		const obj_final = {...obj, paths: paths_final};
		return {path: path_final, obj: obj_final};
	}, [pendingPathUpdatesHere, path, obj, pathIndex]);

	const resolvedMeshes = ResolvePathMeshes(pathAndObj_final.path, pathAndObj_final.obj);

	const materialColor = useMemo(()=>ColorArrayToTHREEColor(fill.color), [fill.color]);

	const textureEntity = GetEntity(fill.texture_id);
	const texture = useBase64ImageStrAsTexture(textureEntity?.image_embedded);

	const geometry = useMemo(()=>{
		const meshes = resolvedMeshes?.meshes;
		if (meshes == null || meshes.length == 0) return null;
		// For the fill, we use the first point-set of the path's resolved meshes. If the path has
		// no transforms (the common case), this is exactly the path's points; when closed, the
		// resolved point-set includes the curve's final point, which is sufficient for a fill.
		const points = meshes[0].map(a=>Vec2.FromSimple(a));
		if (points.length < 3) return null; // a fill needs at least 3 points
		const geom = CreateComplexPolygonMesh(points, [], 0);
		// UVs: world-position-based, so the texture is anchored to the world (like mesh objects).
		// texture_rotation is applied as a UV rotation; texture_size (m) is the UV scale.
		CalculateUVsAsPointPositions(geom, fill.texture_size ?? new Vec2(1, 1), Vec2.zero, fill.texture_rotation ?? 0);
		geom.attributes.position.needsUpdate = true;
		geom.attributes.uv.needsUpdate = true;
		return geom;
	}, [resolvedMeshes, fill.texture_size, fill.texture_rotation]);
	if (geometry == null) return null;

	const opacity = fill.opacity ?? 1;
	return (
		<mesh position={Vec3.zero.NewZ(fill.zOffset ?? 0).To3JPos()}>
			<primitive attach="geometry" object={geometry}/>
			<meshBasicMaterial attach="material" color={materialColor} map={texture} opacity={opacity}
				transparent={true}/>
		</mesh>
	);
});

/** Build a filled polygon mesh (ShapeGeometry; ExtrudeGeometry when height > 0) from outline
 *  points. Local copy of MeshUI's helper, so PathFillUI stays independent of the (to-be-retired)
 *  ObjMesh/MeshUI pipeline. */
function CreateComplexPolygonMesh(points: Vec2[], holes = [], height = 0): ShapeGeometry | ExtrudeGeometry {
	// Create the main shape
	const shape = new THREE.Shape();
	shape.moveTo(points[0].x, points[0].y);
	for (let i = 1; i < points.length; i++) {
		shape.lineTo(points[i].x, points[i].y);
	}

	// Add holes if any
	/*if (holes.length > 0) {
		for (const holePoints of holes) {
			const holePath = new THREE.Path();
			holePath.moveTo(holePoints[0].x, holePoints[0].y);
			for (let i = 1; i < holePoints.length; i++) {
				holePath.lineTo(holePoints[i].x, holePoints[i].y);
			}
			shape.holes.push(holePath);
		}
	}*/

	// create geometry - use ExtrudeGeometry for height if needed
	let geometry;
	if (height > 0) {
		geometry = new ExtrudeGeometry(shape, {
			depth: height,
			bevelEnabled: false
		});
	} else {
		geometry = new ShapeGeometry(shape);
	}
	return geometry;
}